import React, { useState, useEffect } from 'react';
import {
  Grid,
  Box,
  Button,
  Fade
} from '@material-ui/core';
import { TextField } from '@material-ui/core';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { toast } from "react-toastify";
import { makeStyles } from '@material-ui/core/styles';
import { Autocomplete } from "@material-ui/lab";
import DefectService from '../../services/DefectService';
import CliniqueService from '../../services/CliniqueService';

// Icônes Material-UI
import WarningIcon from '@material-ui/icons/Warning';
import CommentIcon from '@material-ui/icons/Comment';
import BlockIcon from '@material-ui/icons/Block';
import { Business } from '@material-ui/icons';

const useStyles = makeStyles((theme) => ({
  formContainer: {
    marginTop: theme.spacing(0.5),
  },
  gridItem: {
    padding: theme.spacing(0.3),
  },
  iconAdornment: {
    color: '#226D68',
    marginRight: theme.spacing(0.8),
    fontSize: '16px !important',
  },
  submitButton: {
    background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
    color: 'white',
    fontWeight: 600,
    fontSize: '0.85rem',
    padding: '8px 24px',
    borderRadius: 6,
    boxShadow: '0 3px 10px rgba(220, 38, 38, 0.3)',
    textTransform: 'none',
    width: '100%',
    marginTop: theme.spacing(0.8),
    '&:hover': {
      background: 'linear-gradient(135deg, #b91c1c 0%, #dc2626 100%)',
      boxShadow: '0 4px 15px rgba(220, 38, 38, 0.4)',
    },
    '&:disabled': {
      background: '#cbd5e1',
      boxShadow: 'none',
    },
  },
  formHeader: {
    textAlign: 'center',
    marginBottom: theme.spacing(1.5),
    padding: theme.spacing(0.8),
  },
  formTitle: {
    fontWeight: 700,
    color: '#dc2626',
    marginBottom: theme.spacing(0.3),
    fontSize: '1.1rem',
  },
  formSubtitle: {
    color: '#64748b',
    fontSize: '0.75rem',
  },
  sectionTitle: {
    fontWeight: 600,
    color: '#226D68',
    marginBottom: theme.spacing(0.8),
    fontSize: '0.85rem',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.4),
  },
  warningSection: {
    backgroundColor: '#fef2f2',
    padding: theme.spacing(1),
    borderRadius: 6,
    border: '1px solid #fecaca',
    marginBottom: theme.spacing(1),
    boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
  },
  compactField: {
    '& .MuiOutlinedInput-root': {
      fontSize: '0.8rem',
      minHeight: '40px',
    },
    '& .MuiInputLabel-root': {
      fontSize: '0.8rem',
      transform: 'translate(14px, 12px) scale(1)',
    },
    '& .MuiInputLabel-shrink': {
      transform: 'translate(14px, -6px) scale(0.75)',
    },
    '& .MuiFormHelperText-root': {
      fontSize: '0.7rem',
      marginTop: 2,
    },
  },
  compactAutocomplete: {
    '& .MuiAutocomplete-inputRoot': {
      paddingTop: '8px !important',
      paddingBottom: '8px !important',
    },
  },
}));

const AddDefectProduct = ({ onClose, refresh }) => {
  const classes = useStyles();
  const [produits, setProduits] = useState([]);

  const listEtats = [
    { name: "EXPIRER", description: "Périmé" },
    { name: "AVARIE", description: "Endommagé" },
    { name: "RETRAIT", description: "Retiré" }
  ];

  const initialValues = {
    codeIdProd: '',
    quan_avarie: '',
    etat: '',
    motif: '',
  };

  const validationSchema = Yup.object().shape({
    codeIdProd: Yup.string().required("Produit obligatoire"),
    quan_avarie: Yup.number()
      .required("Quantité obligatoire")
      .positive("Doit être positif")
      .min(1, "Minimum 1"),
    etat: Yup.string().required("État obligatoire"),
    motif: Yup.string().max(200, "Max 200 caractères"),
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await CliniqueService.getAllProduits();
        setProduits(response.data || []);
      } catch (error) {
        console.error('Erreur chargement produits:', error);
        toast.error("Erreur chargement produits");
      }
    };

    fetchData();
  }, []);

  const onSubmit = async (values, props) => {
    try {
      const produitDefectueux = {
        "codeIdProd": values.codeIdProd,
        "etat_produit": values.etat,
        "quan_avarie": values.quan_avarie,
        "motif": values.motif
      };

      const response = await DefectService.createDefect(produitDefectueux);
      
      if (response?.data?.message) {
        toast.success(`✅ ${response.data.message}`, {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      } else {
        toast.error("❌ Erreur enregistrement", {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      }
      
      props.resetForm();
      
      if (onClose) onClose();
      if (refresh) refresh();
      
    } catch (error) {
      console.error('Erreur enregistrement produit avarié:', error);
      toast.error("❌ Erreur enregistrement", {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    }
  };

  return (
    <Box>
      {/* En-tête compact */}
      <Box className={classes.formHeader}>
        <WarningIcon style={{ fontSize: 28, color: '#dc2626', marginBottom: 2 }} />
        <div className={classes.formTitle}>Produit Défectueux</div>
        <div className={classes.formSubtitle}>Enregistrez un produit avarié</div>
      </Box>

      {/* Section d'avertissement compacte */}
      <Box className={classes.warningSection}>
        <div className={classes.sectionTitle}>
          <BlockIcon style={{ color: '#dc2626', fontSize: 16 }} /> Attention
        </div>
        <div style={{ color: '#7f1d1d', fontSize: '0.7rem', lineHeight: '1.2' }}>
          Vérifiez les informations avant validation.
        </div>
      </Box>

      <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
        {(props) => (
          <Form noValidate className={classes.formContainer}>
            <Grid container spacing={0.8}>
              
              {/* Section produit compacte */}
              <Grid item xs={12}>
                <div className={classes.sectionTitle}>
                  <Business style={{ fontSize: 16 }} /> Produit
                </div>
              </Grid>

              {/* Sélection du produit */}
              <Grid item xs={12} className={classes.gridItem}>
                <Autocomplete
                  id="codeIdProd"
                  name="codeIdProd"
                  options={produits}
                  getOptionLabel={option => option.nomProd || ''}
                  onChange={(e, v) => {
                    props.setFieldValue("codeIdProd", v?.codeIdProd || '');
                  }}
                  className={classes.compactAutocomplete}
                  renderInput={params => (
                    <Field 
                      as={TextField} 
                      {...params} 
                      name="codeIdProd" 
                      label="Produit" 
                      variant="outlined" 
                      fullWidth
                      size="small"
                      className={classes.compactField}
                      error={props.errors.codeIdProd && props.touched.codeIdProd}
                      helperText={<ErrorMessage name='codeIdProd' />}
                      required
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: <Business className={classes.iconAdornment} />
                      }}
                    />
                  )}
                />
              </Grid>

              {/* Quantité et État sur une ligne */}
              <Grid item xs={12} container spacing={0.8}>
                <Grid item xs={6} className={classes.gridItem}>
                  <Field 
                    as={TextField} 
                    name="quan_avarie" 
                    label="Quantité" 
                    fullWidth 
                    variant="outlined"
                    size="small"
                    type="number"
                    className={classes.compactField}
                    error={props.errors.quan_avarie && props.touched.quan_avarie}
                    helperText={<ErrorMessage name='quan_avarie' />}
                    required
                    InputProps={{
                      inputProps: { min: 1, step: 1 }
                    }}
                  />
                </Grid>

                <Grid item xs={6} className={classes.gridItem}>
                  <Autocomplete
                    id="etat"
                    name="etat"
                    options={listEtats}
                    getOptionLabel={option => option.name}
                    onChange={(e, v) => {
                      props.setFieldValue("etat", v?.name || '');
                    }}
                    className={classes.compactAutocomplete}
                    renderInput={params => (
                      <Field 
                        as={TextField} 
                        {...params} 
                        name="etat" 
                        label="État" 
                        variant="outlined" 
                        fullWidth
                        size="small"
                        className={classes.compactField}
                        error={props.errors.etat && props.touched.etat}
                        helperText={<ErrorMessage name='etat' />}
                        required
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: <WarningIcon className={classes.iconAdornment} />
                        }}
                      />
                    )}
                  />
                </Grid>
              </Grid>

              {/* Commentaire compact */}
              <Grid item xs={12}>
                <div className={classes.sectionTitle}>
                  <CommentIcon style={{ fontSize: 16 }} /> Motif
                </div>
              </Grid>

              <Grid item xs={12} className={classes.gridItem}>
                <Field 
                  as={TextField} 
                  name="motif" 
                  label="Commentaire" 
                  fullWidth 
                  variant="outlined"
                  size="small"
                  multiline
                  rows={1}
                  className={classes.compactField}
                  error={props.errors.motif && props.touched.motif}
                  helperText={<ErrorMessage name='motif' />}
                  InputProps={{
                    startAdornment: <CommentIcon className={classes.iconAdornment} />
                  }}
                  placeholder="Raison du défaut (optionnel)"
                />
              </Grid>

              {/* Information sur l'état sélectionné - ultra compacte */}
              {props.values.etat && (
                <Grid item xs={12}>
                  <Fade in timeout={200}>
                    <Box style={{ 
                      padding: '8px 12px', 
                      backgroundColor: '#fffbeb', 
                      border: '1px solid #fcd34d',
                      borderRadius: '6px',
                      color: '#92400e',
                      fontSize: '0.7rem',
                      lineHeight: '1.2',
                    }}>
                      <strong>État:</strong> {props.values.etat} - {
                        listEtats.find(e => e.name === props.values.etat)?.description
                      }
                    </Box>
                  </Fade>
                </Grid>
              )}

              {/* Bouton compact */}
              <Grid item xs={12} className={classes.gridItem}>
                <Button 
                  type='submit' 
                  variant='contained'
                  className={classes.submitButton}
                  startIcon={<WarningIcon style={{ fontSize: 16 }} />}
                  disabled={props.isSubmitting}
                >
                  {props.isSubmitting ? 'En cours...' : 'Enregistrer défaut'}
                </Button>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </Box>
  );
}

export default AddDefectProduct;